/**
 * One app's row on a Connect card: its icon and name, then Connect, a
 * spinner while the browser sign-in runs, or a check once connected. A
 * credential form, a pending check or an error opens below the row.
 */
import { Button } from "@/components/ui/button";
import { Icons } from "@/components/ui/icons";
import { Spinner } from "@/components/ui/spinner";
import { useAuth } from "@/hooks/use-auth";
import { useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { framesConnectionPromptMessages } from "@/i18n/messages/frames-connection-prompt";
import { ConnectionCredentialForm } from "./connection-credential-form";
import { useConnectionSetup } from "./use-connection-setup";

interface SetupProps {
  toolkitSlug: string;
}

/** Remount on identity/toolkit changes so credentials cannot cross accounts. */
export function ConnectionSetup(props: SetupProps) {
  const { user } = useAuth();
  if (!user) return null;
  return (
    <AccountConnectionSetup
      key={`${user.id}:${props.toolkitSlug}`}
      {...props}
    />
  );
}

function AccountConnectionSetup({ toolkitSlug }: SetupProps) {
  const t = useT(framesConnectionPromptMessages);
  const tCommon = useT(commonMessages);
  const row = useConnectionSetup(toolkitSlug);
  const { setup } = row;
  const displayName = setup?.display_name || toolkitSlug;
  const signsInInBrowser = !!setup && !row.apiKey && !row.active;

  return (
    <div
      className="flex flex-col gap-2"
      data-testid={`connection-prompt-row-${toolkitSlug}`}
    >
      <div className="flex items-center gap-2">
        {setup?.icon_url ? (
          <img
            src={setup.icon_url}
            alt=""
            className="h-7 w-7 shrink-0 rounded-control-sm bg-btn-ghost-fill object-contain p-1"
            data-testid="connection-prompt-frame-icon"
          />
        ) : (
          <div
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-control-sm bg-btn-ghost-fill text-secondary"
            data-testid="connection-prompt-frame-icon-fallback"
          >
            <Icons.Plug className="h-3.5 w-3.5" />
          </div>
        )}
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-canvas-sm font-medium text-primary">
            {displayName}
          </span>
          {row.pending ? (
            <span className="text-canvas-2xs text-secondary">
              {t("finishInBrowser")}
            </span>
          ) : (
            signsInInBrowser && (
              <span
                className="text-canvas-2xs text-secondary"
                data-testid="connection-prompt-connect-hint"
              >
                {t("opensBrowserToSignIn")}
              </span>
            )
          )}
        </div>
        {row.active ? (
          <Icons.CheckCircle
            role="img"
            aria-label={t("connectedTo", { app: displayName })}
            className="h-5 w-5 shrink-0 text-accent"
            data-testid="connection-prompt-frame-connected-icon"
          />
        ) : row.pending ? (
          <>
            <Spinner
              className="h-4 w-4 shrink-0"
              data-testid="connection-prompt-frame-spinner"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={row.cancel}
              data-testid="connection-prompt-cancel"
            >
              {tCommon("cancel")}
            </Button>
          </>
        ) : !setup && !row.loadError ? (
          <Spinner
            className="h-4 w-4 shrink-0"
            data-testid="connection-setup-loading"
          />
        ) : (
          signsInInBrowser && (
            <Button
              size="sm"
              onClick={row.connect}
              data-testid="connection-prompt-connect"
            >
              {t("connect")}
            </Button>
          )
        )}
      </div>
      {!row.active && !row.pending && <RowDetails row={row} />}
    </div>
  );
}

/** What opens below a row: an error, a retry, a pending check or the form. */
function RowDetails({ row }: { row: ReturnType<typeof useConnectionSetup> }) {
  const t = useT(framesConnectionPromptMessages);
  const tCommon = useT(commonMessages);
  const error = row.error && (
    <p role="alert" className="text-canvas-sm text-danger">
      {t(row.error)}
    </p>
  );
  if (row.loadError)
    return (
      <div className="flex items-center justify-between gap-2">
        <p role="alert" className="text-canvas-sm text-secondary">
          {t("loadOptionsFailed")}
        </p>
        <Button
          size="sm"
          variant="ghost"
          onClick={row.retryLoad}
          data-testid="connection-setup-retry"
        >
          {tCommon("retry")}
        </Button>
      </div>
    );
  if (!row.setup || !row.apiKey) return error || null;
  if (row.awaitingCredentials)
    return (
      <div className="flex flex-col items-start gap-2">
        {error}
        <p className="text-canvas-sm text-secondary">{t("stillCompleting")}</p>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            disabled={row.busy}
            onClick={row.checkStatus}
            data-testid="connection-credentials-check"
          >
            {t("checkStatus")}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={row.busy}
            onClick={row.replaceCredentials}
            data-testid="connection-credentials-retry"
          >
            {t("useDifferentCredentials")}
          </Button>
        </div>
      </div>
    );
  return (
    <div className="flex flex-col items-start gap-2">
      {error}
      <ConnectionCredentialForm
        setup={row.setup}
        busy={row.busy}
        onSubmit={row.submit}
      />
      {row.error && (
        <Button
          variant="ghost"
          size="sm"
          disabled={row.busy}
          onClick={row.checkStatus}
          data-testid="connection-credentials-check"
        >
          {t("checkConnectionStatus")}
        </Button>
      )}
    </div>
  );
}
